import { getIn } from 'formik';
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import noop from 'lodash/noop';

import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * Per-option <li> that measures its own text width and only mounts a
 * MUI Tooltip when the content actually overflows (long airline name in
 * a narrow dropdown). Avoids paying the Tooltip cost for every option,
 * including in large lists like airports/suppliers.
 */
const AutocompleteOptionItem = ({ liProps, label, body, multiple, isSelected, optionName, isSelectAll }) => {
    const measureRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);

    useLayoutEffect(() => {
        const el = measureRef.current;
        if (!el) return;
        // scrollWidth > clientWidth → text is clipped → tooltip needed.
        // +1 for sub-pixel rounding.
        setOverflowing(el.scrollWidth > el.clientWidth + 1);
    }, [label]);

    const showCheckbox = multiple && !isSelectAll;

    const content = (
        // The checkbox and its label have to share one line. base.scss forces
        // `display: block !important` on every option in the popper (so long
        // single-line options truncate with an ellipsis), which stacked the
        // checkbox above the label and made every row twice as tall. An inline
        // style cannot beat !important, so the fix is a more specific rule
        // keyed on this class — see `.taytan-option-checkbox` in base.scss.
        <li {...liProps} className={`${liProps?.className || ''}${showCheckbox ? ' taytan-option-checkbox' : ''}`}>
            {showCheckbox && (
                <Checkbox checked={isSelected} size="small" style={{ marginRight: 4, padding: 2 }} />
            )}
            <span
                ref={measureRef}
                style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1
                }}
            >
                {body}
            </span>
        </li>
    );

    if (!overflowing) return content;
    return (
        <Tooltip title={label} enterDelay={150} enterNextDelay={100} placement="right">
            {content}
        </Tooltip>
    );
};

const CustomPopper = ({ children = '', style = {}, ...rest }) => {
    return (
        <Popper {...rest} style={{ width: 'auto', minWidth: rest.anchorEl?.offsetWidth || 'auto', ...style }}>
            {children}
        </Popper>
    );
};

const autocompleteByOptionIdStyles = makeStyles(() => ({
    inputStyle: {
        '& > .MuiInputBase-root': {
            padding: '3px 9px',
            flexWrap: 'wrap',
            '& > .MuiInputBase-input.MuiAutocomplete-input': {
                padding: '2px',
                minWidth: 30
            },
            '& > .MuiAutocomplete-endAdornment': {
                top: 'calc(50% - 0px)'
            }
        }
    }
}));

const SELECT_ALL = 'Select All';

const AutocompleteByOptionId = ({
    nameKey = 'name',
    valueKey = '_id',
    field = { value: '', name: '' },
    options = [],
    label = '',
    required = false,
    onChange = null,
    filterOptions,
    renderOption,
    getOptionLabel,
    getOptionSelected,
    helperText = '',
    disabled = false,
    loading = false,
    noOptionsValue = '',
    fullWidth = false,
    className = '',
    textFieldClassName = '',
    classes = {},
    form = { setTouched: noop, setFieldValue: noop },
    fullOption = false,
    disableClearable = false,
    textFieldFullWidth = true,
    variant = 'outlined',
    clearValue = '',
    InputProps = {},
    filterSelectedOptions,
    disableNoOptionsValue = false,
    onOpen = noop,
    multiple = false,
    disableCloseOnSelect = false,
    addSelectAllOption = false,
    defaultValue,
    multipleValues = [],
    height = '',
    placeholder = '',
    showDirections = false,
    freeSolo = false
}) => {
    const styles = autocompleteByOptionIdStyles();

    // Guard against null field (e.g. Formik can't resolve a deep nested path)
    if (!field) return null;

    const { value: fieldValue, name: fieldName = '' } = field;
    const { errors, setTouched, touched, setFieldValue } = form;
    const errorText = useMemo(() => (fieldName ? getIn(errors, fieldName) : ''), [errors, fieldName]);
    const touchedVal = useMemo(() => (fieldName ? getIn(touched, fieldName) : null), [fieldName, touched]);
    const [chosenComponentLevel, setChosenComponentLevel] = useState(defaultValue || '');

    const getHelperText = useMemo(() => {
        if (touchedVal && errorText) {
            return errorText;
        }
        if (showDirections && errorText) {
            return <div style={{ color: 'red', fontSize: '11px', paddingTop: '4px' }}>{errorText}</div>;
        }
        return helperText;
    }, [errorText, helperText, touchedVal]);

    const handleChange = useCallback(
        (ev, value) => {
            //setChosenComponentLevel(value?.name);
            let returnValue = fullOption ? null : clearValue;
            if (typeof value === 'string') {
                // freeSolo: the user typed a value that isn't in the option list.
                returnValue = value;
            } else if (value) {
                const optionValue = fullOption ? value : value[valueKey];
                if (optionValue !== noOptionsValue) {
                    returnValue = fullOption ? value : value[valueKey];
                }
            }
            if (multiple) {
                returnValue = value;
                const selectAllInValue = !!value.find((obj) => obj[nameKey] === SELECT_ALL);
                if (addSelectAllOption) {
                    if (selectAllInValue) {
                        returnValue = options;
                    } else {
                        if (ev.target.innerText === SELECT_ALL) {
                            returnValue = [];
                        }
                    }
                }
            }
            if (onChange) {
                onChange(fieldName, returnValue);
            } else {
                setFieldValue(fieldName, returnValue);
            }
        },
        [
            addSelectAllOption,
            clearValue,
            fieldName,
            fullOption,
            multiple,
            nameKey,
            noOptionsValue,
            onChange,
            options,
            setFieldValue,
            valueKey,
            chosenComponentLevel
        ]
    );

    const value = useMemo(() => {
        // Treat "None" (PG/Python artifact) as null
        let normalizedFieldValue = fieldValue === 'None' ? null : fieldValue;

        // Handle malformed data patterns
        if (typeof normalizedFieldValue === 'object' && normalizedFieldValue) {
            // Case 0: {name: "None"} without a real _id — treat as empty
            if (normalizedFieldValue[nameKey] === 'None' && !normalizedFieldValue[valueKey]) {
                normalizedFieldValue = null;
            }
            // Case 1: {name: {}} or {name: {...}} - extract the inner object or clear
            else if (typeof fieldValue[nameKey] === 'object') {
                if (fieldValue[nameKey]?._id && fieldValue[nameKey]?.name) {
                    // Has valid nested object, extract it
                    normalizedFieldValue = fieldValue[nameKey];
                } else {
                    // Empty or invalid nested object, clear it
                    normalizedFieldValue = null;
                }
            }
        }

        if (multiple) {
            return normalizedFieldValue || [];
        }

        // If null, return default
        if (!normalizedFieldValue) {
            return { [nameKey]: noOptionsValue };
        }

        // If has valueKey, try to match with options
        if (typeof normalizedFieldValue === 'object' && normalizedFieldValue[valueKey]) {
            const match = options.find(
                (opt) =>
                    opt[valueKey] == normalizedFieldValue[valueKey] ||
                    opt._id == normalizedFieldValue._id ||
                    opt._id == normalizedFieldValue[valueKey]
            );
            return match || { [nameKey]: noOptionsValue };
        }

        // If string or number value, find matching option
        if (typeof normalizedFieldValue === 'string' || typeof normalizedFieldValue === 'number') {
            const selectedOption = options.find(
                (option) => option[valueKey] == normalizedFieldValue || option._id == normalizedFieldValue
            );
            if (selectedOption) return selectedOption;
            // No match found — if options is empty, use noOptionsValue to avoid MUI warning
            if (options.length === 0) return { [nameKey]: noOptionsValue };
            return { [nameKey]: normalizedFieldValue || noOptionsValue };
        }

        // Default fallback
        return { [nameKey]: noOptionsValue };
    }, [multiple, fieldValue, valueKey, options, nameKey, noOptionsValue]);

    const handleClose = useCallback(() => {
        setTimeout(() => {
            setTouched({ ...touched, [fieldName]: true });
        }, 0);
    }, [fieldName, setTouched, touched]);

    const autoCompleteValue = useMemo(() => {
        if (multiple) {
            return value;
        }
        if (fullOption) {
            return value || { [nameKey]: noOptionsValue };
        } else {
            return { [nameKey]: value[nameKey] || noOptionsValue };
        }
    }, [fullOption, multiple, nameKey, noOptionsValue, value]);

    const autoCompleteOptions = useMemo(() => {
        if (multiple) {
            if (addSelectAllOption) {
                return [{ [nameKey]: SELECT_ALL, [valueKey]: SELECT_ALL }].concat(options);
            }
            return options;
        }
        if ((value[nameKey] !== noOptionsValue && !noOptionsValue) || disableNoOptionsValue) {
            return options;
        } else {
            return [{ [nameKey]: noOptionsValue }].concat(options);
        }
    }, [
        addSelectAllOption,
        disableNoOptionsValue,
        multiple,
        nameKey,
        noOptionsValue,
        options,
        value,
        valueKey
    ]);

    const filterMultipleValuesOptions = (options, { inputValue }) => {
        const lowerInput = inputValue?.toLowerCase() || '';
        const results = [];
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            const match = multipleValues.some((field) => {
                return option[field] && option[field].toLowerCase().includes(lowerInput);
            });
            if (match) {
                results.push(option);
                // Limit rendered options to avoid heavy DOM when list is large (e.g. 9000 airports)
                if (results.length >= 100) break;
            }
        }
        return results;
    };

    return (
        <Autocomplete
            className={className}
            classes={classes}
            PopperComponent={CustomPopper}
            onOpen={() => {
                onOpen(fieldName);
            }}
            onClose={handleClose}
            options={autoCompleteOptions}
            value={autoCompleteValue}
            filterSelectedOptions={
                multiple
                    ? false
                    : filterSelectedOptions !== undefined
                      ? filterSelectedOptions
                      : !value[nameKey]
            }
            onChange={handleChange}
            freeSolo={freeSolo}
            // MUI hides the dropdown arrow for freeSolo inputs by default, which
            // makes the field look like a plain text box. Force it so the user
            // still sees it's a combobox (pick from the list OR type a value).
            forcePopupIcon={freeSolo || undefined}
            {...(freeSolo && {
                // Store free-typed text (reason 'input') as the field value so the
                // user can enter a value not present in the option list.
                onInputChange: (ev, inputVal, reason) => {
                    if (reason !== 'input') return;
                    if (onChange) onChange(fieldName, inputVal);
                    else setFieldValue(fieldName, inputVal);
                }
            })}
            getOptionLabel={
                getOptionLabel
                    ? getOptionLabel
                    : (option) => {
                          if (typeof option === 'string') return option;
                          if (option && typeof option[nameKey] === 'string') return option[nameKey];
                          return '';
                      }
            }
            isOptionEqualToValue={
                getOptionSelected
                    ? getOptionSelected
                    : (option, value) => {
                          // TODO: Material-UI warning
                          if (addSelectAllOption && option[nameKey] === SELECT_ALL) {
                              if (autoCompleteValue.length + 1 === autoCompleteOptions.length) {
                                  return true;
                              }
                          }

                          // Try multiple ways to match
                          // 1. Match by nameKey
                          if (option[nameKey] === value[nameKey]) return true;

                          // 2. Match by _id if both have it (loose equality for int/string)
                          if (option._id != null && value._id != null && option._id == value._id) return true;

                          // 3. Match by valueKey
                          if (
                              option[valueKey] != null &&
                              value[valueKey] != null &&
                              option[valueKey] == value[valueKey]
                          )
                              return true;

                          // 4. If value is a string/number, match against _id or valueKey
                          if (
                              (typeof value === 'string' || typeof value === 'number') &&
                              (option._id == value || option[valueKey] == value)
                          )
                              return true;

                          return false;
                      }
            }
            filterOptions={
                multipleValues.length
                    ? filterMultipleValuesOptions
                    : createFilterOptions({
                          stringify: filterOptions ? filterOptions : (option) => option[nameKey]
                      })
            }
            renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props;
                // Use _id/valueKey for unique key — name alone can collide (e.g. duplicate supplier names)
                const uniqueKey = option[valueKey] || option._id || key;
                const label = option[nameKey] || '';

                // Detect "CODE - NAME" pattern (airlines: "2J - AIR BURKINA",
                // currencies: "USD - US Dollar", etc.) and render the code in
                // normal weight on the left + the full name in smaller grey
                // on the right with ellipsis on overflow. Falls back to
                // plain rendering when the pattern doesn't match.
                let body;
                if (renderOption) {
                    body = renderOption(option);
                } else {
                    const codeNameMatch =
                        typeof label === 'string' ? label.match(/^([A-Za-z0-9]{2,4})\s*-\s*(.+)$/) : null;
                    if (codeNameMatch) {
                        // Code stays fixed on the left; name (smaller, grey)
                        // takes the remaining space and truncates with
                        // ellipsis when the dropdown is narrow.
                        body = (
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 6,
                                    minWidth: 0,
                                    width: '100%'
                                }}
                            >
                                <span style={{ flexShrink: 0 }}>{codeNameMatch[1]}</span>
                                <span
                                    style={{
                                        color: '#6b7280',
                                        fontSize: '0.85em',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0
                                    }}
                                >
                                    {codeNameMatch[2]}
                                </span>
                            </span>
                        );
                    } else {
                        body = label;
                    }
                }

                return (
                    <AutocompleteOptionItem
                        key={uniqueKey}
                        liProps={rest}
                        label={label}
                        body={body}
                        multiple={multiple}
                        isSelected={selected}
                        optionName={option[nameKey]}
                        isSelectAll={option[nameKey] === SELECT_ALL}
                    />
                );
            }}
            {...(multiple && {
                renderTags: (value) =>
                    value.length > 0 ? (
                        <span style={{ fontSize: '0.875rem', paddingLeft: 4 }}>{value.length} selected</span>
                    ) : null
            })}
            disabled={disabled}
            fullWidth={fullWidth}
            disableClearable={disableClearable || value[nameKey] === noOptionsValue}
            multiple={multiple}
            disableCloseOnSelect={multiple || disableCloseOnSelect}
            renderInput={(params) => (
                <TextField
                    {...params}
                    className={`${styles.inputStyle} ${textFieldClassName}`}
                    label={label}
                    helperText={getHelperText}
                    error={!!touchedVal && !!errorText}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        style: {
                            height: height || 'auto'
                        },
                        ...params.InputProps,
                        ...InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                                {InputProps?.endAdornment || null}
                            </>
                        )
                    }}
                    required={required}
                    fullWidth={textFieldFullWidth}
                    variant={variant}
                    // A multi-select renders its selection as "N selected" in
                    // renderTags, not as input text, so the input still counts
                    // as empty and MUI keeps showing the placeholder — the
                    // field then reads "2 selected Document Type". Drop the
                    // placeholder once something is selected; the compact tag
                    // already says what the field holds.
                    placeholder={
                        multiple && autoCompleteValue?.length
                            ? undefined
                            : chosenComponentLevel || placeholder || '---'
                    }
                />
            )}
        />
    );
};

AutocompleteByOptionId.propTypes = {
    nameKey: PropTypes.string,
    valueKey: PropTypes.string,
    onChange: PropTypes.func,
    onOpen: PropTypes.func,
    field: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.array]),
        name: PropTypes.string
    }),
    label: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({})),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    fullWidth: PropTypes.bool,
    textFieldFullWidth: PropTypes.bool,
    filterOptions: PropTypes.func,
    renderOption: PropTypes.func,
    getOptionLabel: PropTypes.func,
    getOptionSelected: PropTypes.func,
    helperText: PropTypes.string,
    noOptionsValue: PropTypes.string,
    className: PropTypes.string,
    textFieldClassName: PropTypes.string,
    classes: PropTypes.shape({}),
    form: PropTypes.shape({
        errors: PropTypes.shape({}),
        touched: PropTypes.shape({}),
        setTouched: PropTypes.func,
        setFieldValue: PropTypes.func
    }),
    fullOption: PropTypes.bool,
    disableClearable: PropTypes.bool,
    filterSelectedOptions: PropTypes.bool,
    disableNoOptionsValue: PropTypes.bool,
    multiple: PropTypes.bool,
    disableCloseOnSelect: PropTypes.bool,
    addSelectAllOption: PropTypes.bool,
    variant: PropTypes.string,
    InputProps: PropTypes.shape({ endAdornment: PropTypes.any, startAdornment: PropTypes.any }),
    clearValue: PropTypes.any,
    multipleValues: PropTypes.arrayOf(PropTypes.string),
    height: PropTypes.string,
    placeholder: PropTypes.string,
    showDirections: PropTypes.bool,
    freeSolo: PropTypes.bool
};

export default memo(AutocompleteByOptionId);
